import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

export interface SeoConfig {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription?: string;
  canonicalUrl?: string;
  schema: object;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private meta = inject(Meta);
  private titleService = inject(Title);
  private document = inject(DOCUMENT);

  apply(config: SeoConfig): void {
    this.titleService.setTitle(config.title);

    this.meta.updateTag({ name: 'description', content: config.description });

    this.meta.updateTag({ property: 'og:title', content: config.ogTitle });
    this.meta.updateTag({ property: 'og:description', content: config.ogDescription ?? config.description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });

    if (config.canonicalUrl) {
      this.setCanonical(config.canonicalUrl);
    }

    this.setJsonLd(config.schema);
  }

  private setCanonical(url: string): void {
    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setJsonLd(schema: object): void {
    const existing = this.document.getElementById('page-schema');
    if (existing) existing.remove();

    const script = this.document.createElement('script');
    script.id = 'page-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  removePageMeta(): void {
    const schema = this.document.getElementById('page-schema');
    if (schema) schema.remove();
  }
}
